import { ReactElement, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { singletonHook } from "react-singleton-hook";
import useEvent from "react-use-event-hook";

import { ConfirmResult } from "@ryde/shared";

export interface IModalOptions {
  title: string;
  customContent?: ReactNode;
  message?: string | ReactElement;
  showOkButton?: boolean;
  showFooter?: boolean;
  okButtonTitle?: string;
  cancelButtonTitle?: string;
  /** CSS width for the dialog shell; omitted keeps the default confirm width. */
  width?: string;
  /**
   * Blocks confirming — including the Enter shortcut — while the content is not ready to be confirmed.
   * Used by a dialog that opens before its data exists and flips this through `updateModalOptions`.
   */
  isOkButtonDisabled?: boolean;
}

interface IModalConfirm {
  open?: boolean;
  confirmDialogResult: (
    options?: Partial<IModalOptions>,
  ) => Promise<ConfirmResult>;
  modalOptions: IModalOptions;
  handleResult: (result: ConfirmResult) => void;
  /** Merges into the options of the dialog already on screen; untouched keys keep their value. */
  updateModalOptions: (options: Partial<IModalOptions>) => void;
}

const defaultOptions = {
  title: "Are you sure?",
  message: "",
  showOkButton: true,
  showFooter: true,
  okButtonTitle: "Ok",
  cancelButtonTitle: "Cancel",
};

const initialState: IModalConfirm = {
  open: false,
  modalOptions: defaultOptions,
  handleResult: () => undefined,
  confirmDialogResult: () => Promise.resolve(ConfirmResult.CANCEL),
  updateModalOptions: () => undefined,
};

export interface IResolveReject {
  resolveFn?: (result: ConfirmResult) => void;
  rejectFn?: (reason?: string) => void;
}

export const useModalConfirm = singletonHook(initialState, () => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Partial<IModalOptions> | undefined>();
  const [resolveReject, setResolveReject] = useState<IResolveReject | null>(
    null,
  );
  const { resolveFn } = resolveReject || {};

  const confirmDialogResult = useEvent(
    (modalOptions?: Partial<IModalOptions>): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        setOptions(modalOptions);
        setResolveReject({ resolveFn: resolve, rejectFn: reject });
      });
    },
  );

  const updateModalOptions = useEvent(
    (modalOptions: Partial<IModalOptions>): void => {
      setOptions(currentOptions => ({ ...currentOptions, ...modalOptions }));
    },
  );

  const handleClose = useEvent(() => {
    setResolveReject(null);
  });

  const handleResult = useCallback(
    (result: ConfirmResult) => {
      if (resolveFn) {
        resolveFn(result);
        handleClose();
      }
    },
    [resolveFn, handleClose],
  );

  const defaultValuesTranslated = {
    ...defaultOptions,
    okButtonTitle: t(`general.${defaultOptions.okButtonTitle.toUpperCase()}`),
    cancelButtonTitle: t(
      `general.${defaultOptions.cancelButtonTitle.toUpperCase()}`,
    ),
    title: t(`general.ARE_YOU_SURE`),
  };
  return {
    open: resolveReject !== null,
    confirmDialogResult,
    handleResult,
    updateModalOptions,
    modalOptions: { ...defaultValuesTranslated, ...options },
  } as IModalConfirm;
});
